'use client';

import { PLACEHOLDER_IMAGE_SRC } from '@/lib/constants/assets';

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
    ChevronLeft,
    Package,
    Truck,
    MapPin,
    CreditCard,
    ExternalLink,
    AlertCircle,
    Repeat,
} from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useOrder } from '@/hooks/useOrders';
import { useBasePath } from '@/providers/BasePathProvider';
import { useAuth } from '@/providers/AuthProvider';
import { useCart } from '@/providers/CartProvider';
import { useStore } from '@/context/StoreContext';
import { formatPrice, formatDate } from '@/lib/utils/format';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import Image from 'next/image';
import { ShippingApi } from '@/lib/Apis/apis/shipping-api';
import { AXIOS_CONFIG } from '@/lib/Apis/wrapper';
import { useOrderProductData } from '@/hooks/useOrderProductImages';

interface OrderDetailScreenProps {
    id: string;
    /** Guest email from query param — enables public track endpoint when user is not authenticated */
    email?: string;
}

export function OrderDetailScreen({ id, email }: OrderDetailScreenProps) {
    const router = useRouter();
    const { buildPath } = useBasePath();
    const { isAuthenticated } = useAuth();
    // When not authenticated but email is provided, use the public track endpoint.
    // When neither, redirect to login (handled after loading completes).
    const guestEmail = !isAuthenticated ? email : undefined;
    const { order, isLoading, error } = useOrder(id, guestEmail);
    const { reorderFromOrder, isLoading: isCartLoading } = useCart();
    const { setCartOpen } = useStore();
    const [storeAddress, setStoreAddress] = useState<any>(null);
    const [shipment, setShipment] = useState<any>(null);
    const [isReordering, setIsReordering] = useState(false);
    const { images: productImages, names: productNames } = useOrderProductData(order?.items);

    // Fetch store address for pickup orders
    useEffect(() => {
        if (order && order.orderType !== 'Delivery' && !order.pickUpAddress) {
            const fetchStoreAddress = async () => {
                try {
                    const api = new ShippingApi(AXIOS_CONFIG);
                    const res = await api.getStoreAddress();
                    if (res.data) {
                        setStoreAddress(res.data);
                    }
                } catch (e) {
                    console.error('[OrderDetail] Failed to fetch store address:', e);
                }
            };
            fetchStoreAddress();
        }
    }, [order]);

    // Fetch shipment for delivery orders to get the shipping address (source: shipment table)
    useEffect(() => {
        if (order && order.orderType === 'Delivery') {
            const orderId = order.id || order._id;
            console.log('[OrderDetail] Fetching shipment for order ID:', orderId);
            const fetchShipment = async () => {
                try {
                    const api = new ShippingApi(AXIOS_CONFIG);
                    if (orderId) {
                        const res = await api.getOrderShipment(orderId);
                        console.log('[OrderDetail] Shipment fetched:', res.data);
                        if (res.data) {
                            setShipment(res.data);
                        }
                    }
                } catch (e: any) {
                    if (e.response?.status === 404) {
                        console.log('[OrderDetail] No shipment record found in table yet (404). Using fallback address from order object.');
                    } else {
                        console.error('[OrderDetail] Failed to fetch shipment:', e);
                    }
                }
            };
            fetchShipment();
        }
    }, [order]);

    if (isLoading) {
        return (
            <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
                <div className="w-16 h-16 border-4 border-hprimary-20 border-t-primary rounded-full animate-spin mb-4" />
                <p className="text-hmuted font-medium animate-pulse">Retrieving order details...</p>
            </div>
        );
    }

    // Not logged in and no email param — redirect to login
    if (!isAuthenticated && !email && !isLoading) {
        router.push(buildPath(`/login?redirect=/account/orders/${id}`));
        return null;
    }

    if (error || !order) {
        return (
            <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
                <div className="p-4 bg-red-50 rounded-full mb-4">
                    <AlertCircle className="w-10 h-10 text-red-500" />
                </div>
                <h1 className="text-xl font-bold text-hsecondary mb-2">Order Not Found</h1>
                <p className="text-hmuted mb-6">We could not find the order you are looking for.</p>
                <Button onClick={() => router.push(buildPath(isAuthenticated ? '/account' : '/shop'))}>
                    {isAuthenticated ? 'Back to Account' : 'Back to Shop'}
                </Button>
            </div>
        );
    }

    const items = order.items || [];
    const customProducts = (order.customProducts || []) as any[];
    const totalItemCount = items.length + customProducts.length;
    const status = order.orderStatus || 'Pending';
    const isDelivery = order.orderType === 'Delivery';

    const getStatusVariant = (status: string): 'success' | 'warning' | 'primary' | 'danger' | 'gray' => {
        switch (status.toLowerCase()) {
            case 'pending': return 'warning';
            case 'delivered':
            case 'fulfilled':
            case 'picked up': return 'success';
            case 'shipped': return 'primary';
            case 'cancelled': return 'danger';
            default: return 'gray';
        }
    };

    // Fallback logic for various ways addresses might be stored in the order object
    const rawOrder = order as any;
    const shipmentAddress = shipment?.shipmentDetails?.addressTo;
    const shippingAddress = shipmentAddress || order.shippingInfo?.addressTo || rawOrder.shippingAddress || rawOrder.addressTo;
    const pickupAddress = order.pickUpAddress || storeAddress;
    const activeAddress = isDelivery ? shippingAddress : pickupAddress;

    const isCompletedOrder = order.orderStatus &&
        ['Delivered', 'Picked Up', 'Fulfilled'].includes(order.orderStatus);

    const isUnpaidCard = order.payment?.paymentMethod === 'Card' &&
        order.payment?.paymentStatus !== 'Paid' &&
        !!order.payment?.hostedInvoiceUrl;

    const handleReorder = async () => {
        setIsReordering(true);
        try {
            await reorderFromOrder(order);
            setCartOpen(true);
        } finally {
            setIsReordering(false);
        }
    };

    return (
        <div className="min-h-screen py-4 pb-24">
            {/* Header / Navigation */}
            <div className="container mx-auto px-4 pt-8 max-w-6xl">
                <button
                    onClick={() => router.push(buildPath(isAuthenticated ? '/account?tab=orders' : '/shop'))}
                    className="flex items-center gap-1 text-hsecondary/60 hover:text-hsecondary transition-colors mb-6 text-sm pb-4"
                >
                    <ChevronLeft className="w-4 h-4" />
                    {isAuthenticated ? 'Back to Order History' : 'Back to Shop'}
                </button>

                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
                    <div>
                        <div className="flex items-center flex-wrap gap-2 mb-2">
                            <h1 className="text-2xl sm:text-4xl font-medium text-hsecondary">Order Details</h1>
                            <Badge variant={getStatusVariant(status)}>{status}</Badge>
                        </div>
                        <div className="flex items-center flex-wrap gap-x-2 gap-y-1 text-xs text-hsecondary/60 pt-1">
                            <Package className="w-3 h-3 opacity-60 shrink-0" />
                            <span>Order #{id.toUpperCase()}</span>
                            <span className="opacity-40">•</span>
                            <span>{formatDate(order.createdAt || new Date(), 'long')}</span>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        {isAuthenticated && isCompletedOrder && (
                            <Button
                                size="sm"
                                onClick={handleReorder}
                                disabled={isReordering || isCartLoading}
                                className="bg-hsecondary hover:bg-hsecondary/80 text-hsecondary border-none"
                            >
                                <Repeat className="w-4 h-4" />
                                {isReordering ? 'Adding...' : 'Reorder'}
                            </Button>
                        )}
                        {isAuthenticated && order.payment?.hostedInvoiceUrl && (
                            <Button size="sm" onClick={() => window.open(order.payment?.hostedInvoiceUrl, '_blank')} className="bg-hsecondary hover:bg-haccent-dark border-none">
                                <ExternalLink className="w-4 h-4" />
                                {isUnpaidCard ? 'Pay Now' : 'Payment Details'}
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            <div className="container mx-auto px-4 max-w-6xl grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Column: Order Tracking & Items */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Items Section */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-white rounded-3xl border border-slate-200 shadow-xs overflow-hidden pb-4"
                    >
                        <div className="px-4 py-4 sm:p-6 flex items-center justify-between border-b border-slate-100">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-hsecondary/6 rounded-full flex items-center justify-center shrink-0">
                                    <Package className="w-5 h-5 text-hsecondary/60" />
                                </div>
                                <h2 className="text-hsecondary text-lg sm:text-xl font-medium">Order Items</h2>
                            </div>
                            <span className="text-sm text-hsecondary/40">
                                {totalItemCount} {totalItemCount === 1 ? 'Product' : 'Products'}
                            </span>
                        </div>
                        
                        <div className="divide-y divide-slate-100">
                            {items.map((item, idx) => {
                                const variantImage = item.productVariantData?.media?.[0]?.file;
                                const isVariantImagePlaceholder = !variantImage || variantImage === '/placeholder.png';
                                const productId = (item.productVariantData as any).productId;
                                const itemImage = isVariantImagePlaceholder
                                    ? (productImages[productId] || PLACEHOLDER_IMAGE_SRC)
                                    : variantImage;
                                return (
                                <div key={item._id || idx} className="px-4 py-4 sm:px-6 sm:py-5 flex gap-4 group hover:bg-slate-50/50 transition-colors">
                                    <div className="relative w-14 h-14 sm:w-20 sm:h-20 bg-slate-100 rounded-2xl overflow-hidden shrink-0 border border-slate-100 group-hover:scale-105 transition-transform duration-300">
                                        <Image
                                            src={itemImage}
                                            alt={item.productVariantData?.name || 'Item'}
                                            fill
                                            className="object-cover"
                                            sizes="80px"
                                        />
                                    </div>
                                    <div className="flex-1 flex items-center justify-between min-w-0 gap-4">
                                        <div className="min-w-0">
                                            {(() => {
                                                const productName = item.productVariantData?.productName || productNames[productId];
                                                const variantName = item.productVariantData?.name;
                                                const displayName = productName || variantName;
                                                const showVariant = variantName && productName && variantName !== productName;
                                                return (
                                                    <>
                                                        <h3 className="font-medium text-hsecondary text-base leading-snug line-clamp-2">
                                                            {displayName}
                                                        </h3>
                                                        {showVariant && (
                                                            <p className="text-sm text-hsecondary/50 mb-1">{variantName}</p>
                                                        )}
                                                    </>
                                                );
                                            })()}
                                            <p className="text-sm text-hsecondary/40">Qty: {item.quantity} &bull; {formatPrice(item.productVariantData?.finalPrice || 0)} each</p>
                                        </div>
                                        <span className="font-semibold text-hsecondary text-base shrink-0">{formatPrice((item.productVariantData?.finalPrice || 0) * item.quantity)}</span>
                                    </div>
                                </div>
                                );
                            })}
                            {customProducts.map((cp: any, idx: number) => {
                                const name = typeof cp === 'string' ? cp : (cp.name || 'Custom Item');
                                const qty = typeof cp === 'object' ? (cp.quantity || 1) : 1;
                                const price = typeof cp === 'object' ? (cp.price || 0) : 0;
                                return (
                                <div key={`custom-${idx}`} className="px-4 py-4 sm:px-6 sm:py-5 flex gap-4 group hover:bg-slate-50/50 transition-colors">
                                    <div className="relative w-14 h-14 sm:w-20 sm:h-20 bg-slate-100 rounded-2xl overflow-hidden shrink-0 border border-slate-100 group-hover:scale-105 transition-transform duration-300">
                                        <Image
                                            src={PLACEHOLDER_IMAGE_SRC}
                                            alt={name}
                                            fill
                                            className="object-cover"
                                            sizes="80px"
                                        />
                                    </div>
                                    <div className="flex-1 flex items-center justify-between min-w-0 gap-4">
                                        <div className="min-w-0">
                                            <h3 className="font-medium text-hsecondary text-base leading-snug line-clamp-2">{name}</h3>
                                            <p className="text-sm text-hsecondary/40">Qty: {qty} &bull; {formatPrice(price)} each</p>
                                        </div>
                                        <span className="font-semibold text-hsecondary text-base shrink-0">{formatPrice(price * qty)}</span>
                                    </div>
                                </div>
                                );
                            })}
                        </div>
                    </motion.div>

                    {/* Order Meta / Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-white p-4 sm:p-6 rounded-3xl border border-slate-200 shadow-xs">
                            <div className="flex items-center gap-3 pb-4 border-b border-slate-100 mb-4">
                                <div className="w-10 h-10 bg-hsecondary/6 rounded-full flex items-center justify-center shrink-0">
                                    <MapPin className="w-5 h-5 text-hsecondary/60" />
                                </div>
                                <h3 className="text-hsecondary text-lg sm:text-xl font-medium">{isDelivery ? 'Shipping Address' : 'Pickup Location'}</h3>
                            </div>
                            {activeAddress ? (
                                <div className="text-sm text-hsecondary/60 leading-relaxed">
                                    <p className="font-medium text-hsecondary text-base mb-1">
                                        {activeAddress.name}
                                    </p>
                                    <p>{activeAddress.street1}</p>
                                    {activeAddress.street2 && <p>{activeAddress.street2}</p>}
                                    <p>{activeAddress.city}, {activeAddress.state} {activeAddress.zip}</p>
                                    <p>{activeAddress.country}</p>
                                    {activeAddress.phone && (
                                        <div className="mt-2 flex items-center gap-2 text-hsecondary/60 font-medium border-t border-slate-100 py-4 cursor-pointer" onClick={() => window.open(`tel:${activeAddress.phone}`)}>
                                            <span className="text-text-hsecondary/60 font-normal">Phone:</span> {activeAddress.phone}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <p className="text-sm text-hsecondary/60 italic">No address recorded</p>
                            )}
                        </div>
                        <div className="bg-white p-4 sm:p-6 rounded-3xl border border-slate-200 shadow-xs">
                            <div className="flex items-center gap-3 pb-4 border-b border-slate-100 mb-4">
                                <div className="w-10 h-10 bg-hsecondary/6 rounded-full flex items-center justify-center shrink-0">
                                    <CreditCard className="w-5 h-5 text-hsecondary/60" />
                                </div>
                                <h3 className="text-hsecondary text-lg sm:text-xl font-medium">Payment Method</h3>
                            </div>
                            <div className="text-sm text-hsecondary leading-relaxed">
                                <p className="font-medium text-hsecondary text-base mb-1">
                                    {order.payment?.paymentMethod ? order.payment.paymentMethod.replace('_', ' ').toUpperCase() : 'N/A'}
                                </p>
                                <p className="flex items-center gap-2 mt-2">
                                    <span className="text-[11px] uppercase tracking-widest font-bold text-hsecondary/40">Status:</span>
                                    <span
                                        className="text-xs px-2.5 py-1 rounded-full font-medium"
                                        style={order.payment?.paymentStatus === 'Paid'
                                            ? { backgroundColor: '#10B98114', color: '#10B981', border: '1px solid #10B9811A' }
                                            : { backgroundColor: '#FEF3C780', color: '#92400E', border: '1px solid #92400E1A' }
                                        }
                                    >
                                        {order.payment?.paymentStatus || 'Processing'}
                                    </span>
                                </p>
                                {order.payment?.paymentMethod === 'Card' && (
                                    <p className="mt-2 text-sm text-hsecondary/40 tracking-widest">
                                            •••• •••• •••• ••••
                                    </p>
                                )}
                                {isAuthenticated && isUnpaidCard && (
                                    <button
                                        onClick={() => window.open(order.payment?.hostedInvoiceUrl, '_blank')}
                                        className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-3 bg-hsecondary text-white rounded-full font-semibold text-sm hover:bg-hsecondary/90 transition-colors"
                                    >
                                        <CreditCard className="w-4 h-4" />
                                        Pay Now
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Shipment Info Section */}
                    {isDelivery && shipment && (
                        <div className="bg-white p-4 sm:p-6 rounded-3xl border border-slate-200 shadow-xs md:col-span-2">
                            <div className="flex flex-col gap-3 pb-4 border-b border-slate-100 mb-4 sm:flex-row sm:items-center sm:justify-between">
                                <div className="flex min-w-0 items-center gap-3">
                                    <div className="w-10 h-10 bg-hsecondary/6 rounded-full flex items-center justify-center shrink-0">
                                        <Truck className="w-5 h-5 text-hsecondary/60" />
                                    </div>
                                    <h3 className="text-hsecondary text-lg sm:text-xl font-medium leading-tight">Shipment Details</h3>
                                </div>
                                {shipment.shipmentStatus?.status && (
                                    <span
                                        className="w-fit max-w-full text-[11px] sm:text-xs px-2.5 py-1 rounded-full font-medium leading-tight break-words sm:shrink-0"
                                        style={{ backgroundColor: '#FEF3C780', color: '#92400E', border: '1px solid #92400E1A' }}
                                    >
                                        {shipment.shipmentStatus.status}
                                    </span>
                                )}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
                                {shipment.rate && (
                                    <div>
                                        <p className="text-[10px] uppercase tracking-widest font-medium text-hsecondary mb-2">Carrier & Service</p>
                                        <div className="flex items-center gap-3">
                                            {shipment.rate.providerImage75 && (
                                                <div className="w-10 h-10 rounded-xl bg-slate-100 overflow-hidden border border-slate-100 flex items-center justify-center">
                                                    <Image
                                                        src={shipment.rate.providerImage75}
                                                        alt={shipment.rate.provider || 'Carrier'}
                                                        width={40}
                                                        height={40}
                                                        className="object-contain p-1 w-full h-full"
                                                    />
                                                </div>
                                            )}
                                            <div>
                                                <p className="text-sm font-medium text-hsecondary">{shipment.rate.provider}</p>
                                                <p className="text-xs text-hsecondary/40">{shipment.rate.servicelevel?.name}</p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {shipment.shipmentStatus?.trackingNumber ? (
                                    <div>
                                        <p className="text-[10px] uppercase tracking-widest font-medium text-hsecondary mb-2">Tracking Number</p>
                                        <p className="text-sm font-bold text-hsecondary mb-1 font-mono">{shipment.shipmentStatus.trackingNumber}</p>
                                        {shipment.shipmentStatus?.trackingUrlProvider && (
                                            <a
                                                href={shipment.shipmentStatus.trackingUrlProvider}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-xs text-hprimary hover:underline flex items-center gap-1 font-medium"
                                            >
                                                Track Shipment <ExternalLink className="w-3 h-3" />
                                            </a>
                                        )}
                                    </div>
                                ) : (
                                    shipment.rate?.durationTerms && (
                                        <div>
                                            <p className="text-[10px] uppercase tracking-widest font-medium text-hsecondary mb-2">Delivery Estimate</p>
                                            <p className="text-sm text-hsecondary">{shipment.rate.durationTerms}</p>
                                        </div>
                                    )
                                )}
                                {shipment.totalWeight !== undefined && (
                                    <div>
                                        <p className="text-[10px] uppercase tracking-widest font-medium text-hsecondary mb-2">Package Weight</p>
                                        <p className="text-sm text-hsecondary">{shipment.totalWeight} oz</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Right Column: Order Summary */}
                <div className="space-y-6">
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="bg-hsecondary p-5 sm:p-8 rounded-3xl border border-slate-200 shadow-xs lg:sticky lg:top-24"
                    >
                        <h2 className="text-xl font-medium text-white mb-6">
                            Order Summary
                        </h2>

                        <div className="space-y-4 mb-6">
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-white/60">Subtotal</span>
                                <span className="font-medium text-white">{formatPrice(order.subTotal || 0)}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-white/60">Shipping</span>
                                <span className="font-medium text-white">{formatPrice(order.shippingCost || 0)}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-white/60">Tax</span>
                                <span className="font-medium text-white">{formatPrice(order.tax || 0)}</span>
                            </div>
                            {order.discountedAmount !== undefined && order.discountedAmount > 0 && (
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-white/60">Discount</span>
                                    <span className="font-medium text-white">-{formatPrice(order.discountedAmount)}</span>
                                </div>
                            )}
                        </div>

                        <div className="pt-5 border-t border-white/20">
                            <div className="flex justify-between items-center">
                                <p className="text-xs uppercase text-white/60 font-medium">Grand Total</p>
                                <div className="flex items-center gap-2">
                                    <p className="text-2xl font-medium text-white">{formatPrice(order.grandTotal || 0)}</p>
                                    <span className="px-3 py-1 bg-white/10 rounded-full text-[8px] font-bold uppercase tracking-wider text-white/60">
                                        USD
                                    </span>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </div>
    );
}
